// src/lib/crypto.ts
import { auth } from './auth';

const ALGORITHM = 'AES-GCM';
const PBKDF2_ITERATIONS = 100000;
const KEY_LENGTH = 256;
const SALT_LENGTH = 16;
const IV_LENGTH = 12;

// Helper function to convert text to ArrayBuffer
export function textToBuffer(text: string): ArrayBuffer {
  return new TextEncoder().encode(text);
}

// Helper function to convert ArrayBuffer to text
export function bufferToText(buffer: ArrayBuffer): string {
  return new TextDecoder().decode(buffer);
}

// Generate a cryptographically secure random salt
export function generateSalt(): Uint8Array {
  return crypto.getRandomValues(new Uint8Array(SALT_LENGTH));
}

// Derive a crypto key from the user's token
export async function generateKey(
  rawKey: string,
  salt: Uint8Array
): Promise<CryptoKey> {
  try {
    const baseKey = await crypto.subtle.importKey(
      'raw',
      textToBuffer(rawKey),
      'PBKDF2',
      false,
      ['deriveKey']
    );

    return crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt,
        iterations: PBKDF2_ITERATIONS,
        hash: 'SHA-256',
      },
      baseKey,
      { name: ALGORITHM, length: KEY_LENGTH },
      false,
      ['encrypt', 'decrypt']
    );
  } catch (error) {
    console.log(error)
    throw new Error(`Key derivation failed`);
  }
}

// Encrypt data using current user's token
export async function encrypt(
  data: string,
  rawKey: string
): Promise<Uint8Array> {
  try {
    if (!rawKey) throw new Error('Encryption key required');
    
    const salt = generateSalt();
    const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));
    const key = await generateKey(rawKey, salt);
    const dataBuffer = textToBuffer(data);
    
    const encrypted = await crypto.subtle.encrypt(
      { name: ALGORITHM, iv },
      key,
      dataBuffer
    );

    // Combine salt + iv + ciphertext
    const combined = new Uint8Array(salt.length + iv.length + encrypted.byteLength);
    combined.set(salt, 0);
    combined.set(iv, salt.length);
    combined.set(new Uint8Array(encrypted), salt.length + iv.length);
    
    return combined;
  } catch (error) {
    console.log(error);
    throw new Error(`Encryption failed`);
  }
}

// Decrypt data using current user's token
export async function decrypt(
  encryptedData: Uint8Array,
  rawKey: string
): Promise<string> {
  try {
    if (!rawKey) throw new Error('Decryption key required');
    
    // Extract salt (first 16 bytes), iv (next 12 bytes), and ciphertext
    const salt = encryptedData.slice(0, SALT_LENGTH);
    const iv = encryptedData.slice(SALT_LENGTH, SALT_LENGTH + IV_LENGTH);
    const ciphertext = encryptedData.slice(SALT_LENGTH + IV_LENGTH);
    
    const key = await generateKey(rawKey, salt);
    
    const decrypted = await crypto.subtle.decrypt(
      { name: ALGORITHM, iv },
      key,
      ciphertext
    );

    return bufferToText(decrypted);
  } catch (error) {
    console.log(error);
    throw new Error(`Decryption failed`);
  }
}

// Session-aware encryption wrapper
export async function secureEncrypt(data: string): Promise<Uint8Array> {
  const token = await auth.token;
  if (!token) throw new Error('Authentication required for encryption');
  return encrypt(data, token);
}

// Session-aware decryption wrapper
export async function secureDecrypt(encryptedData: Uint8Array): Promise<string> {
  const token = await auth.token;
  if (!token) throw new Error('Authentication required for decryption');
  return decrypt(encryptedData, token);
}